import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatINR, parseNumber } from "@/lib/format";
import { Switch } from "@/components/ui/switch";

export const CompensationInput = ({
  mode,
  setMode,
  monthly,
  setMonthly,
  historicalRate,
  setHistoricalRate,
}) => {
  const annual = useMemo(() => monthly * 12, [monthly]);

  const handleMonthlyChange = (raw) => {
    setMonthly(parseNumber(raw));
  };
  const handleAnnualChange = (raw) => {
    const a = parseNumber(raw);
    setMonthly(a / 12);
  };

  return (
    <section
      className="border border-slate-200 bg-white p-8 print-block"
      data-testid="compensation-input-section"
    >
      <div className="flex items-start justify-between flex-wrap gap-6 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
            Employee Compensation
          </p>
          <h2 className="font-display text-3xl mt-2 text-slate-900">
            Configure the payout basis
          </h2>
        </div>

        <div
          className="flex items-center gap-3 border border-slate-200 rounded-md px-4 py-2 bg-slate-50"
          data-testid="input-mode-toggle-wrap"
        >
          <span
            className={`text-sm font-medium ${
              mode === "monthly" ? "text-slate-900" : "text-slate-400"
            }`}
          >
            Monthly
          </span>
          <Switch
            checked={mode === "annual"}
            onCheckedChange={(v) => setMode(v ? "annual" : "monthly")}
            data-testid="input-mode-toggle"
          />
          <span
            className={`text-sm font-medium ${
              mode === "annual" ? "text-slate-900" : "text-slate-400"
            }`}
          >
            Annual
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
            {mode === "monthly" ? "Monthly Compensation (₹)" : "Annual Compensation (₹)"}
          </Label>
          <Input
            type="text"
            inputMode="numeric"
            className="mt-2 h-12 text-lg font-mono-num border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600"
            value={mode === "monthly" ? monthly || "" : annual || ""}
            onChange={(e) =>
              mode === "monthly"
                ? handleMonthlyChange(e.target.value)
                : handleAnnualChange(e.target.value)
            }
            placeholder={mode === "monthly" ? "e.g. 180000" : "e.g. 2160000"}
            data-testid="input-compensation"
          />
          <p className="text-xs text-slate-500 mt-2" data-testid="derived-compensation-line">
            {mode === "monthly"
              ? `Annual = ${formatINR(annual)}`
              : `Monthly = ${formatINR(monthly)}`}
          </p>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
            Historical Achievement Rate
          </Label>
          <div className="mt-2 flex items-center gap-3">
            <Input
              type="number"
              min="0"
              max="200"
              step="1"
              className="h-12 text-lg font-mono-num border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600"
              value={Math.round(historicalRate * 100)}
              onChange={(e) => {
                const pct = parseFloat(e.target.value) || 0;
                setHistoricalRate(pct / 100);
              }}
              data-testid="input-historical-rate"
            />
            <span className="text-slate-500 font-mono-num text-lg">%</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Used for expected-revenue projection.
          </p>
        </div>

        <div className="border-l border-slate-200 pl-6">
          <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">
            Total Annual Payout
          </p>
          <p
            className="font-display text-4xl mt-2 text-slate-900"
            data-testid="annual-payout-display"
          >
            {formatINR(annual)}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Base salary considered fully paid.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CompensationInput;
